/**
 * handleHttpError
 *
 * Normalizes axios errors into a structured payload:
 *   { httpStatus: number | null, message: string, raw: any }
 *
 * Returns null for canceled / aborted requests so callers can bail out early.
 */

const HTTP_ERROR_MESSAGES = {
    400: "Bad request. Please check your input and try again.",
    401: "Your session has expired. Please refresh the page.",
    403: "You are not authorized to perform this action.",
    404: "The requested resource was not found.",
    408: "The request timed out. Please try again.",
    409: "A conflict occurred. Please refresh and try again.",
    422: "Invalid data submitted. Please check your input.",
    429: "Too many requests. Please wait a moment and try again.",
    500: "An internal server error occurred. Please try again later.",
    502: "The server received an invalid response. Please try again.",
    503: "The service is temporarily unavailable. Please try again in a few minutes.",
    504: "The server took too long to respond. Please try again.",
};

/**
 * @param {Error} error - The error thrown by axios.
 * @returns {{ httpStatus: number|null, message: string, raw: any } | null}
 *   Returns null if the request was intentionally canceled/aborted.
 */
export function handleHttpError(error) {
    // Intentionally canceled requests — caller should return early, not reject.
    if (
        error.name === "CanceledError" ||
        error.name === "AbortError" ||
        error.code === "ERR_CANCELED"
    ) {
        return null;
    }

    const httpStatus = error.response?.status ?? null;
    const raw = error.response?.data ?? error.message ?? null;

    // Pick a friendly message for known status codes, otherwise fall back.
    const message =
        HTTP_ERROR_MESSAGES[httpStatus] ||
        raw?.detail ||
        raw?.message ||
        (typeof raw === "string" ? raw : null) ||
        error.message ||
        "An unexpected error occurred. Please try again.";

    return { httpStatus, message, raw };
}
